export function isBidLimitError(error) {
  return error?.message === 'BID_SUBSCRIPTION_REQUIRED';
}

export function allowanceLabel(allowance) {
  return allowance.subscribed ? 'Unlimited bids with your subscription'
    : `${allowance.remaining} of ${allowance.freeLimit} free bids left this month`;
}

export const currentTermsVersion = '2026-09-26';
export async function acceptBiddingTerms(client, accepted) {
  if (accepted !== true) throw new Error('Please accept the terms to continue.');
  const { error } = await client.rpc('accept_current_terms', { policy_version: currentTermsVersion });
  if (error) throw new Error(error.message);
}

export async function getBidAllowance(client, organizationId) {
  const { data, error } = await client.rpc('get_bid_allowance', { organization_uuid: organizationId });
  if (error) throw new Error(error.message);
  const row = Array.isArray(data) ? data[0] : data;
  if (!row || row.organization_id !== organizationId) throw new Error('Bid allowance is unavailable. Please retry.');
  return {
    organizationId: row.organization_id, periodStart: row.period_start, resetsAt: row.resets_at,
    used: row.used, freeLimit: row.free_limit, remaining: row.remaining,
    subscribed: row.subscribed, canBid: row.can_bid,
  };
}

// No checkout endpoint: v1 sells the subscription through Apple in-app purchase only.
// The portal stays so an existing Stripe subscriber can still manage or cancel.
const endpoints = new Set(['subscription-status', 'create-subscription-portal']);

/** What the billing page offers for a company's subscription status. */
export function subscriptionChoices(status) {
  if (!status) return { subscribeInApp: false, manageApple: false, manageStripe: false };
  if (!status.subscribed) return { subscribeInApp: true, manageApple: false, manageStripe: false };
  return {
    subscribeInApp: false,
    manageApple: status.provider === 'apple',
    manageStripe: Boolean(status.canManage) && (status.provider === 'stripe' || status.managementProvider === 'stripe'),
  };
}
export async function requestBilling(client, endpoint, body, fetcher = fetch) {
  if (!endpoints.has(endpoint)) throw new Error('Unknown billing operation.');
  const { data, error } = await client.auth.getSession();
  if (error || !data?.session) throw new Error('Sign in to manage billing.');
  const userId = data.session.user.id;
  const response = await fetcher(`/.netlify/functions/${endpoint}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.session.access_token}` },
    body: JSON.stringify(body),
  });
  const result = await response.json().catch(() => ({}));
  const current = await client.auth.getSession();
  if (current.data?.session?.user.id !== userId) throw new Error('Your account changed. Please try again.');
  if (!response.ok) {
    const cause = new Error(result.code === 'BILLING_NOT_CONFIGURED'
      ? 'Subscriptions are not available yet. Your existing bids remain available.'
      : result.message || result.error || 'Could not load billing. Please retry.');
    cause.code = result.code;
    throw cause;
  }
  return result;
}

export function stripeCheckoutUrl(value) {
  let url;
  try { url = new URL(value); } catch { throw new Error('Invalid checkout link.'); }
  if (url.protocol !== 'https:' || !['checkout.stripe.com', 'billing.stripe.com'].includes(url.hostname) || url.username || url.password) {
    throw new Error('Invalid checkout link.');
  }
  return url.href;
}
